import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useJobsite } from "../context/JobsiteContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/app/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { fmtDate } from "../lib/utils";
import { exportInspectionPdf } from "../lib/pdfExport";
import { FileText, ImagePlus, X, CheckCircle2, XCircle, MinusCircle, Download, RefreshCw, Pencil, Trash2, Loader2, Plus, ArrowLeft, ArrowRight } from "lucide-react";

const STATUS_STYLE = {
  Draft: "bg-zinc-800 text-white border-zinc-500 font-bold",
  "In Progress": "bg-amber-500/20 text-amber-400 border-amber-500/50 font-bold",
  "In Review": "bg-blue-500/20 text-blue-400 border-blue-500/50 font-bold",
  Closed: "bg-emerald-500/20 text-emerald-400 border-emerald-500/50 font-bold",
};

const CHECKPOINT_STYLE = {
  "Hold Point": "bg-destructive/10 text-destructive border-destructive/40",
  "Witness Point": "bg-[hsl(var(--status-hold))/0.1] text-[hsl(var(--status-hold))] border-[hsl(var(--status-hold))/0.4]",
  "Review Point": "bg-primary/10 text-primary border-primary/40",
};

const RESPONSE_TYPES = ["Pass/Fail", "Yes/No", "Text", "Number", "Date", "Signature", "Photo"];
const CHECKPOINT_TYPES = ["Hold Point", "Witness Point", "Review Point"];

const BACKEND_ORIGIN = "https://webapp-sitesync.onrender.com";
function resolveAttachmentUrl(url) {
  if (!url) return url;
  return url.startsWith("/") ? `${BACKEND_ORIGIN}${url}` : url;
}

function computeBreakdown(results) {
  let total = 0;
  let inspected = 0;
  let pass = 0;
  let fail = 0;
  let na = 0;
  let open = 0;

  (results || []).forEach((r) => {
    const snap = r.item_snapshot || r;
    const itemType = snap.item_type || snap.type;
    const hasResponse = snap.response_type || snap.checkpoint_type || snap.acceptance_criteria;
    const isHeader = itemType === "Section Header" || itemType === "header" || (!hasResponse && r.children?.length > 0);

    if (!isHeader) {
      total += 1;
      const st = r.status_selection || "";
      const hasText = typeof r.text_value === "string" && r.text_value.trim().length > 0;
      const hasNum = r.numeric_value !== null && r.numeric_value !== undefined && String(r.numeric_value).trim() !== "";
      const hasDate = typeof r.date_value === "string" && r.date_value.trim().length > 0;
      const hasPhotos = Array.isArray(r.attachments) && r.attachments.length > 0;

      const isItemAnswered = (st !== "" && st !== "Open") || hasText || hasNum || hasDate || hasPhotos;

      if (isItemAnswered) {
        inspected += 1;
        if (st === "Pass" || st === "Yes") pass += 1;
        else if (st === "Fail" || st === "No") fail += 1;
        else if (st === "N/A") na += 1;
        else pass += 1; 
      } else {
        open += 1;
      }
    }
  });

  return { total, inspected, pass, fail, na, open };
}

export default function Inspections() {
  const { activeSite } = useJobsite();
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [selected, setSelected] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState(""); // [web-busca]

  const load = useCallback(async ({ hard = false } = {}) => {
    if (hard) setInspections([]);
    const q = activeSite ? `?project_id=${activeSite.id}` : "";
    try {
      const { data } = await api.get(`/itp/inspections${q}`, { headers: { "Cache-Control": "no-cache" }, params: { _: Date.now() } });
      setInspections(data);
    } catch (e) {
      toast.error("Failed to load inspections");
      setInspections([]);
    }
  }, [activeSite]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    if (!routeId) {
      setSelected(null);
      return;
    }
    api.get(`/itp/inspections/${routeId}`)
      .then(({ data }) => { if (!cancelled) setSelected(data); })
      .catch(() => { if (!cancelled) toast.error("Failed to load inspection"); });
    return () => { cancelled = true; };
  }, [routeId]);

  const hardSync = async () => {
    setRefreshing(true);
    try { await load({ hard: true }); toast.success("Refreshed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); }
    finally { setRefreshing(false); }
  };

  if (selected) {
    return (
      <InspectionDetail
        inspection={selected}
        onClose={() => {
          navigate("/app/inspections");
          load({ hard: true });
        }}
      />
    );
  }

  const q = query.trim().toLowerCase();
  const shown = q
    ? inspections.filter((i) => [i.number, i.title, i.location_details, i.project_name].some((v) => String(v || "").toLowerCase().includes(q)))
    : inspections;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <PageHeader
        title="Inspections"
        subtitle="Digital ITP execution."
        action={<Button size="sm" variant="outline" className="rounded-sm border-border" onClick={hardSync} disabled={refreshing}><RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh</Button>}
      />
      {inspections.length === 0 ? (
        <EmptyState title="No inspections yet" body="Clone an ITP template to a project." action={<Link to="/app/admin/itp-templates"><Button className="rounded-sm bg-primary hover:bg-primary/90">Go to templates <ArrowRight className="inline h-4 w-4 ml-1" /></Button></Link>} />
      ) : (
        <>
        <div className="mb-3 flex items-center gap-3">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by number, name, location or project..." className="max-w-sm rounded-sm" data-testid="inspections-search" />
          <span className="text-xs text-muted-foreground" data-testid="inspections-count">{shown.length} of {inspections.length}</span>
        </div>
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 label-overline text-left">
              <tr><Th>No.</Th><Th>Name</Th><Th>Rev</Th><Th>Project</Th><Th>Location</Th><Th>Status</Th><Th>Created</Th><Th></Th></tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="inspections-nomatch">No inspections match your search.</td></tr>
              )}
              {shown.map((i) => (
                <tr key={i.id} className="border-t border-border hover:bg-accent/30">
                  <Td mono>{i.number || "—"}</Td>
                  <Td><div className="flex items-start gap-2"><FileText className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span className="min-w-0 whitespace-pre-line break-words font-medium">{i.title || i.template_snapshot?.title || "Untitled"}</span></div></Td>
                  <Td mono>{i.revision || i.template_snapshot?.revision || "—"}</Td>
                  <Td>{i.project_name || "—"}</Td>
                  <Td mono>{i.location_details || "—"}</Td>
                  <Td><Badge variant="outline" className={`rounded-sm font-mono uppercase tracking-widest text-[10px] ${STATUS_STYLE[i.status] || STATUS_STYLE.Draft}`}>{i.status || "Draft"}</Badge></Td>
                  <Td mono>{fmtDate(i.created_at)}</Td>
                  <Td>
                    <Link
                      to={`/app/inspections/${i.id}`}
                      className="inline-flex items-center justify-center rounded-sm text-xs font-mono px-3 py-1.5 border border-border bg-background hover:bg-accent text-foreground transition-colors"
                    >
                      Open <ArrowRight className="inline h-3.5 w-3.5 ml-0.5" />
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

// [web-info] Um campo da secao "General information": titulo pequeno e o valor embaixo.
const InfoItem = ({ label, value, mono }) => (
  <div className="min-w-0">
    <div className="label-overline mb-1">{label}</div>
    <div className={`whitespace-pre-line break-words text-sm ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
  </div>
);

const Th = ({ children }) => <th className="px-4 py-2 font-mono text-[10px] tracking-widest">{children}</th>;
const Td = ({ children, mono }) => <td className={`px-4 py-3 ${mono ? "font-mono text-xs" : ""}`}>{children}</td>;

function InspectionDetail({ inspection, onClose }) {
  const [ins, setIns] = useState(inspection);
  const [holdBlocked, setHoldBlocked] = useState(null);
  const [editingDetails, setEditingDetails] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", number: "", location_details: "", description: "" });
  const [addingItem, setAddingItem] = useState(false);
  const [newItem, setNewItem] = useState({ title: "", checkpoint_type: "", response_type: "" });
  const [savingItem, setSavingItem] = useState(false);
  const { company, user } = useAuth();

  const reload = async () => { 
    const { data } = await api.get(`/itp/inspections/${ins.id}`); 
    setIns(data); 
  };

  const rawResults = ins.results || ins.items || ins.checklist || [];
  const breakdown = useMemo(() => computeBreakdown(rawResults), [rawResults]);
  const canEditDetails = user?.role === "admin" || user?.role === "supervisor";
  const isClosed = ins.status === "Closed";
  const canAddItems = user?.role === "admin" && !isClosed;

  const changeStatus = async (newStatus, isAuto = false) => {
    if (ins.status === newStatus) return;
    try {
      await api.patch(`/itp/inspections/${ins.id}`, { status: newStatus });
      setIns((prev) => ({ ...prev, status: newStatus }));
      if (!isAuto) {
        toast.success(`Status → ${newStatus}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  useEffect(() => {
    if (!ins?.id) return;

    if (breakdown.inspected > 0 && ins.status === "Draft") {
      changeStatus("In Progress", true);
    } else if (breakdown.inspected === 0 && ins.status === "In Progress") {
      changeStatus("Draft", true);
    }
  }, [breakdown.inspected, ins.status, ins.id]);

  const openEditDetails = () => {
    setEditForm({ title: ins.title || "", number: ins.number || "", location_details: ins.location_details || "", description: ins.description || "" });
    setEditingDetails(true);
  };

  const saveDetails = async () => {
    try {
      await api.patch(`/itp/inspections/${ins.id}`, editForm);
      toast.success("Details updated");
      setEditingDetails(false);
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this inspection?")) return;
    try {
      await api.delete(`/itp/inspections/${ins.id}`);
      toast.success("Inspection deleted");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const exportPdf = async () => {
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
    await new Promise((resolve) => setTimeout(resolve, 300));
    try {
      const { data: freshIns } = await api.get(`/itp/inspections/${ins.id}`);
      setIns(freshIns);
      const freshResults = freshIns.results || freshIns.items || freshIns.checklist || [];
      exportInspectionPdf({ inspection: freshIns, company, breakdown: computeBreakdown(freshResults) });
    } catch (e) {
      exportInspectionPdf({ inspection: ins, company, breakdown });
    }
  };

  const updateResult = async (rid, patch) => {
    try {
      const { data } = await api.patch(`/itp/inspection-results/${rid}`, patch);
      
      setIns((p) => {
        const listKey = p.results ? "results" : p.items ? "items" : "checklist";
        const updatedList = (p[listKey] || []).map((r) => (r.id === rid ? data : r));
        
        const newBreakdown = computeBreakdown(updatedList);
        let nextStatus = p.status;
        if (newBreakdown.inspected > 0 && p.status === "Draft") {
          nextStatus = "In Progress";
          api.patch(`/itp/inspections/${p.id}`, { status: "In Progress" }).catch(() => {});
        } else if (newBreakdown.inspected === 0 && p.status === "In Progress") {
          nextStatus = "Draft";
          api.patch(`/itp/inspections/${p.id}`, { status: "Draft" }).catch(() => {});
        }

        return { ...p, status: nextStatus, [listKey]: updatedList };
      });

      setHoldBlocked(null);
    } catch (e) {
      const detail = e.response?.data?.detail || "Save failed";
      if (e.response?.status === 409) setHoldBlocked({ rid, message: detail });
      toast.error(detail);
    }
  };

  const addAttachment = async (rid, file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/itp/inspection-results/${rid}/attachments/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await reload();
    } catch (e) { toast.error("Upload failed"); }
  };

  const removeAttachment = async (rid, aid) => { 
    await api.delete(`/itp/inspection-results/${rid}/attachments/${aid}`); 
    await reload(); 
  };

  const openAddItem = () => {
    setNewItem({ title: "", checkpoint_type: "", response_type: "" });
    setAddingItem(true);
  };

  const saveNewItem = async () => {
    if (!newItem.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSavingItem(true);
    try {
      await api.post(`/itp/inspections/${ins.id}/results`, {
        title: newItem.title.trim(),
        checkpoint_type: newItem.checkpoint_type || null,
        response_type: newItem.response_type || null,
      });
      toast.success("Item added");
      setAddingItem(false);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add item");
    } finally {
      setSavingItem(false);
    }
  };

  const flatResultsList = useMemo(() => {
    const resultsList = [...rawResults].sort((a, b) => (a.sort_index ?? 0) - (b.sort_index ?? 0));
    const byId = {};
    const roots = [];
    resultsList.forEach((r) => { byId[r.id] = { ...r, children: [] }; });
    resultsList.forEach((r) => {
      const parentId = r.parent_result_id || r.parent_id || r.item_snapshot?.parent_id || r.item_snapshot?.parent_item_id;
      if (parentId && byId[parentId]) byId[parentId].children.push(byId[r.id]);
      else roots.push(byId[r.id]);
    });
    const flatten = (nodes, parentPrefix = "", out = []) => {
      nodes.forEach((n, index) => {
        const currentNum = parentPrefix ? `${parentPrefix}.${index + 1}` : `${index + 1}`;
        out.push({ node: n, depth: parentPrefix ? parentPrefix.split(".").length : 0, displayNumber: currentNum });
        if (n.children?.length > 0) flatten(n.children, currentNum, out);
      });
      return out;
    };
    return flatten(roots);
  }, [rawResults]);

  const inspectionTitle = ins.title || ins.template_snapshot?.title || "Untitled Inspection";
  const inspectionRevision = ins.revision || ins.template_snapshot?.revision || "1";
  const currentStatus = ins.status || "Draft";

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-sm border-border" onClick={onClose}><ArrowLeft className="h-4 w-4 mr-1.5" /> Back</Button>
        <div className="label-overline">INSPECTION DETAIL</div>
        <div className="ml-auto flex gap-2">
          {canEditDetails && <Button size="sm" variant="outline" className="rounded-sm border-border" onClick={openEditDetails}><Pencil className="h-4 w-4 mr-1.5" /> Edit Details</Button>}
          <Button size="sm" className="rounded-sm bg-primary hover:bg-primary/90" onClick={exportPdf}><Download className="h-4 w-4 mr-1.5" /> Save as PDF</Button>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 bg-muted flex items-center justify-center font-mono font-bold text-lg rounded-sm text-muted-foreground">{company?.name?.slice(0, 2).toUpperCase() || "SI"}</div>
          <div>
            <div className="label-overline text-muted-foreground">{company?.name || "SITESYNC HQ"}</div>
            <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xl font-bold tracking-tight">
              {ins.number ? <span className="font-mono text-primary" data-testid="header-number">{ins.number}</span> : null}
              <span className="whitespace-pre-line break-words">{inspectionTitle}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">{ins.project_name || "—"} {ins.location_details ? `• ${ins.location_details}` : ""} • Rev {inspectionRevision}</div>
          </div>
        </div>

        <Select value={currentStatus} onValueChange={(val) => changeStatus(val)}>
          <SelectTrigger className={`rounded-sm font-mono uppercase tracking-widest text-xs px-3 py-2 w-48 border-2 shadow-md transition-all ${STATUS_STYLE[currentStatus] || STATUS_STYLE.Draft}`}>
            <SelectValue>{currentStatus}</SelectValue>
          </SelectTrigger>
          <SelectContent className="rounded-sm font-mono uppercase text-xs bg-zinc-900 border-zinc-700 text-foreground">
            <SelectItem value="Draft" className="text-zinc-100 font-bold focus:bg-zinc-800">● Draft</SelectItem>
            <SelectItem value="In Progress" className="text-amber-400 font-bold focus:bg-zinc-800">● In Progress</SelectItem>
            <SelectItem value="In Review" className="text-blue-400 font-bold focus:bg-zinc-800">● In Review</SelectItem>
            <SelectItem value="Closed" className="text-emerald-400 font-bold focus:bg-zinc-800">● Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <details open className="rounded-sm border border-border bg-card p-5" data-testid="general-info">
        <summary className="label-overline cursor-pointer select-none">General information</summary>
        <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
          <InfoItem label="Name" value={inspectionTitle} />
          <InfoItem label="No." value={ins.number} mono />
          <InfoItem label="Status" value={currentStatus} />
          <InfoItem label="Revision" value={`Rev ${inspectionRevision}`} />
          <InfoItem label="Project" value={ins.project_name} />
          <InfoItem label="Location" value={ins.location_details} />
          <InfoItem label="Created" value={ins.created_at ? fmtDate(ins.created_at) : ""} />
          <InfoItem label="Created by" value={ins.created_by_name} />
        </div>
        {ins.description ? (
          <div className="mt-4">
            <div className="label-overline mb-1">Description</div>
            <div className="whitespace-pre-line break-words text-sm" data-testid="general-description">{ins.description}</div>
          </div>
        ) : null}
      </details>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-border bg-card rounded-sm p-5 flex flex-col justify-between md:col-span-3">
          <div className="label-overline mb-3">Inspection Item Status</div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 py-4">
            <div className="relative h-32 w-32 flex items-center justify-center">
              <svg className="h-full w-full -rotate-90 transform" viewBox="0 0 36 36">
                <path
                  className="text-muted"
                  strokeWidth="3.8"
                  stroke="currentColor"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                {breakdown.total > 0 && breakdown.pass > 0 && (
                  <path
                    className="text-[hsl(var(--status-pass))]"
                    strokeDasharray={`${(breakdown.pass / breakdown.total) * 100} 100`}
                    strokeDashoffset="0"
                    strokeWidth="3.8"
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                )}
                {breakdown.total > 0 && breakdown.fail > 0 && (
                  <path
                    className="text-destructive"
                    strokeDasharray={`${(breakdown.fail / breakdown.total) * 100} 100`}
                    strokeDashoffset={`${-(breakdown.pass / breakdown.total) * 100}`}
                    strokeWidth="3.8"
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                )}
                {breakdown.total > 0 && breakdown.na > 0 && (
                  <path
                    className="text-muted-foreground"
                    strokeDasharray={`${(breakdown.na / breakdown.total) * 100} 100`}
                    strokeDashoffset={`${-((breakdown.pass + breakdown.fail) / breakdown.total) * 100}`}
                    strokeWidth="3.8"
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                )}
              </svg>
              <div className="absolute flex flex-col items-center justify-center text-center">
                <span className="text-xl font-bold font-mono">{breakdown.inspected}/{breakdown.total}</span>
                <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">Inspected</span>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-1 gap-2 text-xs font-mono">
              <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--status-pass))]" /> Pass / Done: {breakdown.pass}</div>
              <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-destructive" /> Fail: {breakdown.fail}</div>
              <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-muted-foreground" /> N/A: {breakdown.na}</div>
              <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-border" /> Open: {breakdown.open}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-2">
        <div className="label-overline mb-3">Checklist — Inspection items</div>
        <div className="space-y-3">
          {flatResultsList.map(({ node: r, depth, displayNumber }) => {
            const snap = r.item_snapshot || r;
            const itemType = snap.item_type || snap.type;
            const hasResponse = snap.response_type || snap.checkpoint_type || snap.acceptance_criteria;
            const isHeader = itemType === "Section Header" || itemType === "header" || (!hasResponse && r.children?.length > 0);
            if (isHeader && !snap.response_type) return <SectionRow key={r.id} result={r} depth={depth} displayNumber={displayNumber} />;
            return (
              <ItemRow
                key={r.id}
                result={r}
                displayNumber={displayNumber}
                depth={depth}
                disabled={isClosed}
                holdBlocked={holdBlocked?.rid === r.id ? holdBlocked.message : null}
                onUpdate={(patch) => updateResult(r.id, patch)}
                onAddAttachment={(file) => addAttachment(r.id, file)}
                onRemoveAttachment={(aid) => removeAttachment(r.id, aid)}
              />
            );
          })}
        </div>

        {canAddItems && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm border-border border-dashed mt-3 w-full"
            onClick={openAddItem}
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add Item
          </Button>
        )}
      </div>

      <Dialog open={editingDetails} onOpenChange={setEditingDetails}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Edit Inspection Details</DialogTitle>
            <DialogDescription>Update the name, number, location and description.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* [web-nome-numero] */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><label className="label-overline mb-1 block">Name</label><Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="rounded-sm font-mono" data-testid="edit-name" /></div>
              <div><label className="label-overline mb-1 block">No.</label><Input value={editForm.number} onChange={(e) => setEditForm({ ...editForm, number: e.target.value })} placeholder="e.g. G.09" className="rounded-sm font-mono" data-testid="edit-number" /></div>
            </div>
            <div><label className="label-overline mb-1 block">Location</label><Input value={editForm.location_details} onChange={(e) => setEditForm({ ...editForm, location_details: e.target.value })} className="rounded-sm font-mono" /></div>
            <div>
              <label className="label-overline mb-1 block">Description</label>
              <textarea 
                value={editForm.description} 
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} 
                className="flex min-h-[80px] w-full rounded-sm border border-input bg-background px-3 py-2 text-sm font-mono" 
              />
            </div>
          </div>
          <DialogFooter className="flex justify-between items-center sm:justify-between w-full">
            {canEditDetails && (
              <Button variant="destructive" size="sm" className="rounded-sm" onClick={handleDelete}>
                <Trash2 className="h-4 w-4 mr-1.5" /> Delete Inspection
              </Button>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditingDetails(false)}>Cancel</Button>
              <Button onClick={saveDetails} className="rounded-sm bg-primary hover:bg-primary/90">Save</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addingItem} onOpenChange={setAddingItem}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Add Item</DialogTitle>
            <DialogDescription>Add a new checklist item to this inspection only.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="label-overline mb-1 block">Title *</label>
              <Input
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                placeholder="e.g. Additional photo evidence"
                className="rounded-sm font-mono"
              />
            </div>
            <div>
              <label className="label-overline mb-1 block">Checkpoint Type</label>
              <Select value={newItem.checkpoint_type} onValueChange={(v) => setNewItem({ ...newItem, checkpoint_type: v })}>
                <SelectTrigger className="rounded-sm font-mono text-sm">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent className="rounded-sm font-mono text-sm">
                  {CHECKPOINT_TYPES.map((ct) => (
                    <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-overline mb-1 block">Response Type *</label>
              <Select value={newItem.response_type} onValueChange={(v) => setNewItem({ ...newItem, response_type: v })}>
                <SelectTrigger className="rounded-sm font-mono text-sm">
                  <SelectValue placeholder="Select a response type" />
                </SelectTrigger>
                <SelectContent className="rounded-sm font-mono text-sm">
                  {RESPONSE_TYPES.map((rt) => (
                    <SelectItem key={rt} value={rt}>{rt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddingItem(false)}>Cancel</Button>
            <Button onClick={saveNewItem} disabled={savingItem} className="rounded-sm bg-primary hover:bg-primary/90">
              {savingItem ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionRow({ result, depth = 0, displayNumber }) {
  const snap = result.item_snapshot || result;
  const title = snap.title || snap.activity_description || snap.name || "Section";
  return (
    <div className="border-l-4 border-primary bg-primary/5 rounded-sm p-4 mt-4 relative" style={{ marginLeft: `${depth * 24}px` }}>
      <div className="flex items-center gap-3">
        <span className="font-mono text-sm font-semibold text-primary min-w-[3rem]">{displayNumber}.</span>
        <div className="flex-1 min-w-0">
          <div className="whitespace-pre-line break-words text-base font-semibold tracking-tight">{title}</div>
          {snap.description && <div className="whitespace-pre-line text-xs text-muted-foreground mt-0.5">{snap.description}</div>}
        </div>
      </div>
    </div>
  );
}

function ItemRow({ result, displayNumber, depth = 0, disabled, holdBlocked, onUpdate, onAddAttachment, onRemoveAttachment }) {
  const snap = result.item_snapshot || result;
  
  const [comments, setComments] = useState(result.comments || "");
  const [textValue, setTextValue] = useState(result.text_value || "");
  const [numericValue, setNumericValue] = useState(result.numeric_value ?? "");
  const [dateValue, setDateValue] = useState(result.date_value || "");
  const commentRef = useRef(null);

  useEffect(() => {
    setComments(result.comments || "");
    setTextValue(result.text_value || "");
    setNumericValue(result.numeric_value ?? "");
    setDateValue(result.date_value || "");
  }, [result]);

  const autoGrowComment = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  useEffect(() => {
    autoGrowComment(commentRef.current);
  }, [comments]);

  const negative = result.status_selection === "Fail" || result.status_selection === "No";
  const commit = (patch) => onUpdate({ comments, ...patch });

  const handleToggleSelect = (option) => {
    if (disabled) return;
    const currentSel = result.status_selection || "";
    const isAlreadySelected = currentSel === option;
    const newSelection = isAlreadySelected ? "" : option;
    commit({ status_selection: newSelection });
  };

  const itemTitle = snap.title || snap.activity_description || snap.name || "Untitled Item";

  const rt = snap.response_type;
  const isPhotoType = !["Pass/Fail", "Yes/No", "Number", "Date", "Signature", "Text"].includes(rt);

  return (
    <div className={`border border-border bg-card rounded-sm p-5 relative ${negative ? "border-destructive/40" : ""}`} style={{ marginLeft: `${depth * 24}px` }}>
      <div className="flex items-start justify-between gap-3 pr-10">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-mono text-xs font-semibold text-primary min-w-[3rem]">{displayNumber}.</span>
            {snap.checkpoint_type && <Badge variant="outline" className={`rounded-sm font-mono uppercase tracking-widest text-[10px] ${CHECKPOINT_STYLE[snap.checkpoint_type] || ""}`}>{snap.checkpoint_type}</Badge>}
            {snap.response_type && <Badge variant="outline" className="rounded-sm font-mono uppercase tracking-widest text-[10px]">{snap.response_type}</Badge>}
          </div>
          <div className="whitespace-pre-line break-words text-sm font-medium" data-testid="item-title">{itemTitle}</div>
          {snap.description && <div className="whitespace-pre-line text-xs text-muted-foreground mt-1">{snap.description}</div>}
          {snap.acceptance_criteria && <div className="whitespace-pre-line text-xs text-muted-foreground mt-1"><span className="font-mono uppercase tracking-widest text-[10px] mr-1">Acceptance</span>{snap.acceptance_criteria}</div>}
        </div>
      </div>
      <div className="mt-4">
        <ResponseInput 
          result={result} 
          disabled={disabled} 
          onSelect={handleToggleSelect} 
          textValue={textValue} 
          setTextValue={setTextValue} 
          commitText={(v) => commit({ text_value: v })} 
          numericValue={numericValue} 
          setNumericValue={setNumericValue} 
          commitNumeric={(v) => commit({ numeric_value: v })} 
          dateValue={dateValue} 
          setDateValue={setDateValue} 
          commitDate={(v) => commit({ date_value: v })} 
          onAddAttachment={onAddAttachment} 
          onRemoveAttachment={onRemoveAttachment} 
        />
      </div>

      {isPhotoType && (
        <div className="mt-3">
          <div className="label-overline mb-1">Comments</div>
          <textarea
            ref={commentRef}
            value={comments}
            onChange={(e) => {
              setComments(e.target.value);
              autoGrowComment(e.target);
            }}
            onBlur={() => onUpdate({ comments })}
            placeholder="Write a comment..."
            rows={1}
            className="flex min-h-[40px] max-h-[50vh] w-full resize-none overflow-y-auto rounded-sm border border-input bg-background px-3 py-2 text-sm font-mono"
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}

// [web-na] Pass/Fail e Yes/No ja trazem o N/A. Os outros tipos (numero, data, texto, assinatura, foto)
// ganham um botao N/A embaixo; marcado, os campos ficam apagados. O que ja foi preenchido nao e apagado.
function ResponseInput(props) {
  const { result, disabled, onSelect } = props;
  const rt = (result.item_snapshot || result).response_type;
  if (rt === "Pass/Fail" || rt === "Yes/No") return <ResponseField {...props} />;
  const isNA = (result.status_selection || "") === "N/A";
  return (
    <div className="space-y-3">
      <div className={isNA ? "pointer-events-none select-none opacity-40" : ""} data-testid="item-inputs">
        <ResponseField {...props} />
      </div>
      <div className="flex gap-1">
        <ToggleBtn label="N/A" icon={MinusCircle} color="na" active={isNA} disabled={disabled} onClick={() => onSelect("N/A")} />
      </div>
    </div>
  );
}

function ResponseField({ result, disabled, onSelect, textValue, setTextValue, commitText, numericValue, setNumericValue, commitNumeric, dateValue, setDateValue, commitDate, onAddAttachment, onRemoveAttachment }) {
  const snap = result.item_snapshot || result;
  const rt = snap.response_type;
  const currentSel = result.status_selection || "";

  if (rt === "Pass/Fail" || rt === "Yes/No") {
    const opts = rt === "Pass/Fail" ? ["Pass", "Fail"] : ["Yes", "No"];
    return (
      <div className="flex gap-1">
        {opts.map((o) => (
          <ToggleBtn key={o} label={o} icon={o === "Pass" || o === "Yes" ? CheckCircle2 : XCircle} color={o === "Pass" || o === "Yes" ? "pass" : "fail"} active={currentSel === o} disabled={disabled} onClick={() => onSelect(o)} />
        ))}
        <ToggleBtn label="N/A" icon={MinusCircle} color="na" active={currentSel === "N/A"} disabled={disabled} onClick={() => onSelect("N/A")} />
      </div>
    );
  }
  if (rt === "Number") return <Input type="number" value={numericValue} onChange={(e) => setNumericValue(e.target.value)} onBlur={() => commitNumeric(numericValue === "" ? null : Number(numericValue))} className="rounded-sm font-mono max-w-sm" disabled={disabled} />;
  if (rt === "Date") return <Input type="date" value={dateValue?.slice(0, 10) || ""} onChange={(e) => setDateValue(e.target.value)} onBlur={() => commitDate(dateValue)} className="rounded-sm font-mono max-w-sm" disabled={disabled} />;
  
  if (rt === "Signature") {
    return <SignaturePad result={result} disabled={disabled} onSave={(dataUrl) => commitText(dataUrl)} />;
  }

  if (rt === "Text") return <textarea value={textValue} onChange={(e) => setTextValue(e.target.value)} onBlur={() => commitText(textValue)} placeholder="Field notes" className="flex min-h-[60px] w-full rounded-sm border border-input bg-background px-3 py-2 text-sm font-mono" disabled={disabled} />;
  return <PhotoAttachments result={result} disabled={disabled} onAdd={onAddAttachment} onRemove={onRemoveAttachment} />;
}

function SignaturePad({ result, disabled, onSave }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const fillWhiteBackground = (canvas) => {
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    
    if (result.text_value) {
      const img = new Image();
      img.onload = () => {
        fillWhiteBackground(canvas);
        ctx.drawImage(img, 0, 0);
        setHasDrawn(true);
      };
      img.src = result.text_value;
    } else {
      fillWhiteBackground(canvas);
      setHasDrawn(false);
    }
  }, [result.text_value]);

  const startDrawing = (e) => {
    if (disabled) return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    if (!isDrawing || disabled) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    onSave(dataUrl);
  };

  const clearCanvas = () => {
    if (disabled) return;
    const canvas = canvasRef.current;
    fillWhiteBackground(canvas);
    setHasDrawn(false);
    onSave("");
  };

  return (
    <div className="space-y-2">
      <div className="border border-border rounded-sm bg-card overflow-hidden inline-block relative touch-none">
        <canvas
          ref={canvasRef}
          width={320}
          height={140}
          className="cursor-crosshair bg-white block"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!hasDrawn && !disabled && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-muted-foreground text-xs font-mono">
            Sign here
          </div>
        )}
      </div>
      {!disabled && (
        <div>
          <Button size="sm" type="button" variant="outline" onClick={clearCanvas} className="rounded-sm text-xs h-7">
            Clear Signature
          </Button>
        </div>
      )}
    </div>
  );
}

function ToggleBtn({ label, icon: Icon, color, active, disabled, onClick }) {
  const styles = { pass: "border-[hsl(var(--status-pass))] bg-[hsl(var(--status-pass))] text-white", fail: "border-destructive bg-destructive text-destructive-foreground", na: "border-muted-foreground bg-muted-foreground text-background" }[color];
  const activeCls = active ? styles : "border-border text-muted-foreground hover:bg-accent";
  return <button type="button" onClick={onClick} disabled={disabled} className={`h-16 w-24 rounded-sm border-2 font-mono uppercase text-xs font-bold tracking-widest flex flex-col items-center justify-center gap-1 transition-all ${activeCls} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}><Icon className="h-5 w-5" />{label}</button>;
}

function PhotoAttachments({ result, disabled, onAdd, onRemove }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        await onAdd(file);
      }
    } catch (err) {
      console.error("Photo upload failed:", err);
    } finally {
      setUploading(false);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {(result.attachments || []).map((att) => (
          <div key={att.id} className="relative group h-20 w-20 border border-border rounded-sm overflow-hidden bg-background">
            <img
              src={resolveAttachmentUrl(att.url)}
              alt="attachment"
              className="h-full w-full object-cover cursor-zoom-in"
              onClick={() => setLightboxUrl(att.url)}
            />
            {!disabled && (
              <button
                type="button"
                onClick={() => onRemove(att.id)}
                className="absolute top-1 right-1 h-5 w-5 bg-destructive text-destructive-foreground rounded-full grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
        {!disabled && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="h-20 w-20 border border-dashed border-border rounded-sm grid place-items-center text-muted-foreground hover:border-primary hover:text-primary transition-colors bg-card"
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <ImagePlus className="h-5 w-5" />
            )}
          </button>
        )}
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*"
        multiple
        className="hidden"
      />

      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-6"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={resolveAttachmentUrl(lightboxUrl)}
            alt="attachment full size"
            className="max-h-full max-w-full object-contain rounded-sm"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
